import AutoCompleteSectionWithTag from '@/components/AutoCompleteSectionWithTag';
import CustomCard from '@/components/customCard';
import { LocationConditionEnum, Plan, UnionCountry } from '@/constants/enum';
import { unionCountries } from '@/constants/options';
import { getRegionNameCountryIn } from '@/helpers/string';
import useLocationOptions from '@/hooks/useLocationOptions';
import useUserPlan from '@/hooks/useUserPlan';
import { apiCaller } from '@/redux/query';
import { clickedDiscardSelector } from '@/redux/slice/appbar.slice';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import ruleSlice, { ruleErrorsSelector, ruleSettingsSelector } from '@/redux/slice/rule.slice';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { IAutoComplete, IOptions } from '@/types';
import { IConditionValue } from '@/types/newSetup.type';
import { Badge } from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import { Tone } from '@shopify/polaris/build/ts/src/components/Badge';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import ConnectBadge from '../ConnectBadge';
import checkoutRuleSlice, {
  checkoutRuleErrorsSelector,
  checkoutRuleSettingsSelector,
  currentOrderItemValueSelector,
} from '@/redux/slice/checkoutRule.slice';
import { REST_OF_THE_WORLD } from '@/constants';

interface IProps {
  onDismiss: () => void;
  badgeTone?: Tone;
  hasConnectBadge?: boolean;
  badgeContent?: string;
  data: IConditionValue[];
  handleChange: (value: IConditionValue[]) => void;
  isInCheckout?: boolean;
}

const CountryCondition = (props: IProps) => {
  const OPTION_ALL = '*';

  const { id } = useParams();
  const dispatch = useDispatch();
  const { isPlanLimited } = useUserPlan();

  const isSkip = useSelector(isSkipApiSelector);
  const dataSettings = useSelector(dataSettingsSelector);
  const currentOrderItemValue = useSelector(currentOrderItemValueSelector);

  const dataNormalRule = useSelector(ruleSettingsSelector);
  const dataCheckoutRule = useSelector(checkoutRuleSettingsSelector);
  const dataRuleLocations = useMemo(() => {
    return props.isInCheckout ? dataCheckoutRule.checkoutLocationConditions : dataNormalRule.locationConditions;
  }, [dataCheckoutRule, dataNormalRule, props.isInCheckout]);

  const errorsNormal = useSelector(ruleErrorsSelector);
  const errorsCheckout = useSelector(checkoutRuleErrorsSelector);

  const errors = useMemo(() => {
    return props.isInCheckout ? errorsCheckout : errorsNormal;
  }, [errorsCheckout, errorsNormal, props.isInCheckout]);

  const clickedDiscard = useSelector(clickedDiscardSelector);

  const [isInit, setIsInit] = useState(true);
  const [state, setState] = useState<IOptions[]>([]);
  const [deselectedCountryOptions, setDeselectedCountryOptions] = useState<IAutoComplete.Section[]>([]);
  const locationOptions = useLocationOptions({
    countries: true,
    union: true,
  });

  const shopifyCountry = apiCaller.useGetCountriesQuery(undefined, { skip: isSkip });
  const currentSlice = useMemo(() => {
    return props.isInCheckout ? checkoutRuleSlice : ruleSlice;
  }, [props.isInCheckout]);

  const handleSetError = useCallback(
    (msg: string) => {
      dispatch(
        currentSlice.actions.handleSetError({
          value: { field: 'country', msg },
        }),
      );
    },
    [currentSlice.actions, dispatch],
  );

  useEffect(() => {
    const selectedValue = state.map((item) => item.value);
    const checkCountryInUnion = (unionCode: string, countryCode: string) => {
      return unionCountries[unionCode as keyof typeof unionCountries]?.includes(countryCode);
    };

    const res = locationOptions.countryOptions.map((item) => {
      let disable = false;
      let badgeContent = '';
      let badge = disable || !shopifyCountry.data?.data?.code?.split(',')?.find((code) => code === item.value.toLowerCase());
      //disable country if in state country has region include country is selected
      for (let country of selectedValue) {
        if (checkCountryInUnion(country, item.value)) {
          disable = true;
          if (id) {
            badge = true;
            badgeContent = 'Existed in ' + getRegionNameCountryIn(item.value as keyof typeof unionCountries);
          }
          break;
        }
      }

      if (isPlanLimited) {
        disable = !selectedValue.includes(item.value);
      }

      return {
        ...item,
        disabled: disable,
        badge,
        badgeContent,
      };
    });

    //handle generate region country options
    const unions = locationOptions.unionOptions?.map((item) => {
      let checkHasOptionInRegionSelected = false;
      if (state.length && (item.value === OPTION_ALL || item.value === REST_OF_THE_WORLD)) {
        checkHasOptionInRegionSelected = true;
      } else {
        state.forEach((country) => {
          //check if country in state is in list region
          if (checkCountryInUnion(item.value, country.value)) {
            checkHasOptionInRegionSelected = true;
            return;
          }
        });
      }

      return {
        ...item,
        disabled: checkHasOptionInRegionSelected,
        badge: checkHasOptionInRegionSelected,
        badgeContent: checkHasOptionInRegionSelected
          ? item.value === OPTION_ALL
            ? 'Have countries selected'
            : 'Have countries existed in this region'
          : '',
      };
    });

    const optionAll = unions.find((item) => item.value === OPTION_ALL);
    const rest = unions?.filter((item) => item.value !== OPTION_ALL && item.value !== REST_OF_THE_WORLD);
    const restOfTheWorld = unions?.find((item) => item.value === REST_OF_THE_WORLD);
    //case select all country => disable all others option
    if (selectedValue?.includes(OPTION_ALL)) {
      setDeselectedCountryOptions([
        {
          title: '',
          options: [
            {
              ...optionAll,
              //@ts-ignore: next-line
              disabled: optionAll?.value !== OPTION_ALL,
              badge: optionAll?.value !== OPTION_ALL,
              badgeContent: 'In selected region',
            },
            {
              ...restOfTheWorld,
              //@ts-ignore: next-line
              disabled: true,
              badge: true,
              badgeContent: 'In selected region',
            },
          ],
        },
        {
          title: 'Region',
          options: rest.map((item) => ({
            ...item,
            disabled: true,
            badge: true,
            badgeContent: 'In selected region',
          })),
        },
        {
          title: 'Country',
          options: res.map((item) => ({
            ...item,
            disabled: true,
            badge: true,
            badgeContent: 'In selected region',
          })),
        },
      ]);
    }

    const handleGenerateOptions = (groupOption: IOptions[], type: 'all' | 'region' | 'country' | 'rest') => {
      for (let country of selectedValue) {
        if (Object.keys(UnionCountry)?.includes(country as UnionCountry)) {
          const isSelectedOptionAll = state.some((item) => item.value === OPTION_ALL);
          const isSelectedOptionRestOfWorld = state.some((item) => item.value === REST_OF_THE_WORLD);
          groupOption = groupOption.map((item) => {
            if (isSelectedOptionAll || isSelectedOptionRestOfWorld) {
              return { ...item, disabled: true, badge: true, badgeContent: 'Existed in selected region' };
            }
            if (checkCountryInUnion(country, item.value)) {
              return {
                ...item,
                badge: true,
                badgeContent:
                  item.badgeContent || `Existed in ${UnionCountry[country as keyof typeof UnionCountry] || 'selected'}`,
              };
            }
            return item;
          });
        }
      }
      switch (type) {
        case 'all':
        case 'rest':
        case 'region': {
          const isOptionAllSelected = state.some((item) => item.value === OPTION_ALL);
          const isOptionRestSelected = state.some((item) => item.value === REST_OF_THE_WORLD);
          const isPlanLimited = dataSettings.plan !== Plan.PRO && dataSettings.plan !== Plan.SHOPIFY_PLUS;

          return groupOption.map((item) => {
            if (isPlanLimited) {
              return {
                ...item,
                disabled: true,
                badge: true,
                badgeContent: 'Upgrade required',
              };
            }

            if (isOptionAllSelected) {
              return {
                ...item,
                disabled: type !== 'all',
                badge: type !== 'all',
                badgeContent: type === 'all' ? '' : 'In selected region',
              };
            }
            if (isOptionRestSelected) {
              return {
                ...item,
                disabled: type !== 'rest',
                badge: type !== 'rest',
                badgeContent: type === 'rest' ? '' : 'In selected region',
              };
            }
            return item;
          });
        }

        default:
          return groupOption;
      }
    };

    setDeselectedCountryOptions([
      {
        title: '',
        options: [
          ...handleGenerateOptions(optionAll ? [optionAll as IOptions] : [], 'all'),
          ...handleGenerateOptions(restOfTheWorld ? [restOfTheWorld as IOptions] : [], 'rest'),
        ],
      },
      {
        title: 'Region',
        options: handleGenerateOptions(rest, 'region'),
      },
      {
        title: 'Country',
        options: handleGenerateOptions(
          res.sort((a, b) => +a.badge - +b.badge),
          'country',
        ),
      },
    ]);
  }, [
    locationOptions.countryOptions,
    locationOptions.unionOptions,
    id,
    isPlanLimited,
    state,
    shopifyCountry.data?.data,
    dataSettings.plan,
  ]);

  const handleCountryChange = useCallback(
    (value: IOptions[]) => {
      if (value.length) handleSetError('');
      setState(value);
      const data = value.map((item) => ({ data: item.value, countryStateName: item.label }));
      let newDataLocationConditions = dataRuleLocations.filter((item) => {
        if (item.type === LocationConditionEnum.State) {
          dispatch(currentSlice.actions.handleSetError({ value: { field: 'state', msg: '' } }));
          return false;
        }
        return true;
      });

      const oldIndexCountry = newDataLocationConditions.findIndex((item) => item.type === LocationConditionEnum.Country);
      if (oldIndexCountry !== -1) {
        const current = newDataLocationConditions[oldIndexCountry];
        newDataLocationConditions.splice(oldIndexCountry, 1, {
          type: LocationConditionEnum.Country,
          value: data,
          order: current?.order ?? currentOrderItemValue,
        });
        if (!current?.order || Number.isNaN(+current?.order)) {
          //@ts-expect-error
          if (props.isInCheckout) dispatch(currentSlice.actions.handleAddCurrentCount());
        }
      } else {
        newDataLocationConditions.push({ type: LocationConditionEnum.Country, value: data, order: currentOrderItemValue });
        //@ts-expect-error
        if (props.isInCheckout) dispatch(currentSlice.actions.handleAddCurrentCount());
      }

      dispatch(
        currentSlice.actions.handleChange({
          //@ts-ignore-next-line
          key: props.isInCheckout ? 'checkoutLocationConditions' : 'locationConditions',
          value: newDataLocationConditions,
        }),
      );

      if (newDataLocationConditions.length) {
        handleSetError('');
      } else {
        handleSetError('Please select at least 1 country');
      }
    },
    [currentOrderItemValue, currentSlice.actions, dataRuleLocations, dispatch, handleSetError, props.isInCheckout],
  );

  useEffect(() => {
    if (dataRuleLocations && isInit) {
      setIsInit(false);
      const countryCondition = dataRuleLocations.find((item) => item.type === LocationConditionEnum.Country);
      if (countryCondition?.value?.length && countryCondition?.value?.every((item) => item.data)) {
        const newState = countryCondition.value.map((item) => ({ value: item.data, label: item.countryStateName || '' }));
        setState(newState);
        handleSetError('');
      }
    }
  }, [dataRuleLocations, handleSetError, isInit]);

  useEffect(() => {
    if (clickedDiscard) {
      setIsInit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickedDiscard]);

  return (
    <div>
      {!!props.hasConnectBadge && <ConnectBadge tone={props.badgeTone}>{props.badgeContent}</ConnectBadge>}
      <CustomCard
        title={
          <div>
            <span className="mr-8">Countries, regions</span>
            <Badge>{state.length + ''}</Badge>
          </div>
        }
        textClassName="bold-text-styled"
        secondaryActions={{
          onAction: props.onDismiss,
          buttonProps: {
            icon: DeleteIcon,
            tone: 'critical',
            variant: 'plain',
          },
        }}
        collapsible
        defaultExpanded
      >
        <AutoCompleteSectionWithTag
          requiredIndicator
          allowMultiple
          badge={<Badge tone="warning">Not in Shopline Markets</Badge>}
          deselectedOptions={deselectedCountryOptions}
          tag={state}
          label="Select a country or countries"
          selectedOptions={state.map((item) => item.value)}
          setSelectedOptions={handleCountryChange}
          customErrorMessage={errors.find((item) => item.field === 'country')?.msg}
          customeHandleError={handleSetError}
        />
      </CustomCard>
    </div>
  );
};

export default memo(CountryCondition);
